import { Visibility as VisibilityIcon, VisibilityOff as VisibilityOffIcon } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
	Box,
	Button,
	CircularProgress,
	Container,
	IconButton,
	InputAdornment,
	Link,
	TextField,
	Typography
} from "@mui/material";
import { FC, FormEvent, useEffect, useState } from "react";
import { FieldValues, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { ReactComponent as LoginImage } from "../../assets/images/login-image.svg";
import MMVDLogoSrc from "../../assets/logos/mmvd-logo.png";
import { useRecaptcha } from "../../hooks/useRecaptcha";
import { useAppDispatch, useAppSelector } from "../../redux";
import {
	clearAuthState,
	loginUser,
	useRequestOTPQuery,
	useVerifyLoginOTPMutation,
	verifyOTP
} from "../../redux/reducers/auth.reducer";
import { removeRecaptchaScript } from "../../utils/commonUtils";
import "./login.css";
import OTPInput from "./OTPInput";

const Login: FC = () => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();

	const authState = useAppSelector((state) => state.auth);

	// Add reCAPTCHA hook
	const { executeRecaptcha, isEnabled: isRecaptchaEnabled } = useRecaptcha();

	const [showPassword, setShowPassword] = useState<boolean>(false);
	const [otp, setOtp] = useState<string>("");
	const [resendTimer, setResendTimer] = useState<number>(0);

	// APIS
	// REQUEST OTP
	const {
		isLoading: requestOTPLoading,
		isFetching: requestOTPFetching,
		error: requestOTPError,
		isSuccess: requestOTPSuccess,
		refetch: refetchOTP
	} = useRequestOTPQuery(null, {
		skip: !authState.user.token || authState.user.token === "invalid" || authState.otpVerified,
		refetchOnMountOrArgChange: true
	});

	// VERIFY LOGIN OTP
	const [verifyLoginOTP, { isLoading: verifyLoginOTPLoading, error: verifyLoginOTPError }] =
		useVerifyLoginOTPMutation();

	const {
		reset,
		register,
		handleSubmit,
		formState: { errors }
	} = useForm();

	async function handleSubmitForm(data: FieldValues) {
		// Get reCAPTCHA token (if enabled)
		let recaptchaToken: string | null = null;

		if (isRecaptchaEnabled) {
			try {
				recaptchaToken = await executeRecaptcha("login");
				console.log("reCAPTCHA token obtained");
			} catch (error) {
				console.warn("Failed to get reCAPTCHA token, proceeding without it:", error);
				// Don't block login if reCAPTCHA fails (fail-open on frontend too)
			}
		}

		// Dispatch login with optional reCAPTCHA token
		dispatch(
			loginUser({
				username: data.username,
				password: data.password,
				...(recaptchaToken && { recaptcha_token: recaptchaToken }) // Only add if token exists
			})
		);
	}

	function handleToggleShowPassword() {
		setShowPassword(!showPassword);
	}

	function handleSubmitOTP(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		verifyLoginOTP(otp)
			.unwrap()
			.then(() => {
				dispatch(verifyOTP());
			})
			.catch(() => {});
	}

	function handleRetryRequestOTP() {
		if (resendTimer === 0) {
			refetchOTP()
				.unwrap()
				.then(() => setResendTimer(60))
				.catch(() => {});
		}
	}

	function changeAccount() {
		dispatch(clearAuthState());
	}

	useEffect(() => {
		reset({
			username: "",
			password: ""
		});
	}, [reset]);

	useEffect(() => {
		if (authState.user.token && authState.user.token !== "invalid" && authState.otpVerified) {
			// Remove reCAPTCHA script and badge after successful login
			removeRecaptchaScript();

			if (authState.user.permissions) {
				if (authState.user.permissions.dashboard.view) {
					navigate("/dashboard");
					return;
				}

				if (authState.user.permissions.violation_logs.view) {
					navigate("/violation-logs");
					return;
				}

				if (authState.user.permissions.rto_metadata?.view) {
					navigate("/jurisdictions");
					return;
				}

				if (authState.user.permissions.user_management.view) {
					navigate("/roles-permissions/user-roles");
					return;
				}

				if (authState.user.permissions.user_activity_logs) {
					navigate("/configuration/activity-logs");
					return;
				}

				if (authState.user.permissions.user_session_management.view) {
					navigate("/user-management/active-sessions");
					return;
				}

				if (authState.user.permissions.interceptor_vehicles.view) {
					navigate("/configuration/interceptors");
				}
			}

			navigate("/configuration/about-us");
		}
	}, [
		authState.defaultPasswordError,
		authState.otpVerified,
		authState.resetPasswordError,
		authState.user.permissions,
		authState.user.token,
		navigate
	]);


	// Timer effect
	useEffect(() => {
		let timer: NodeJS.Timeout;
		if (resendTimer > 0) {
			timer = setInterval(() => {
				setResendTimer((prev) => prev - 1);
			}, 1000);
		}
		return () => {
			if (timer) clearInterval(timer);
		};
	}, [resendTimer]);

	// Start timer when OTP is requested successfully
	useEffect(() => {
		if (requestOTPSuccess) {
			setResendTimer(60);
		}
	}, [requestOTPSuccess]);

	useEffect(() => {
		if (
			requestOTPError &&
			"data" in requestOTPError &&
			requestOTPError.data &&
			String(requestOTPError.data).toLowerCase() === "invalid token."
		) {
			changeAccount();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [requestOTPError]);

	return (
		<Box className="login-component-wrapper">
			<Container maxWidth="xl" sx={{ height: "100%" }}>
				<Box className="login-content-wrapper">
					<Box className="login-image-section">
						{/* <EdgeTrackLogo width="80%" fill="var(--color-primary-main)" /> */}

						<Box sx={{ display: "flex", alignItems: "stretch", justifyContent: "center", gap: "1rem" }}>
							{/* <MTESLogo height="100px" /> */}
							<img src={MMVDLogoSrc} alt="MMVD" height="100px" />
							<Box>
								<Typography fontFamily="Patua One" fontWeight={500} fontSize="42px" whiteSpace="nowrap" color="primary">
									MTES
								</Typography>

								<Typography variant="body2" fontFamily="Patua One" fontSize="18px" whiteSpace="nowrap" color="primary">
									Mobile Traffic Enforcement System
								</Typography>
							</Box>
						</Box>

						<LoginImage width="90%" />
					</Box>

					{authState.user.token && authState.user.token !== "invalid" ? (
						requestOTPLoading || requestOTPFetching ? (
							<Box className="login-form-section">
								<CircularProgress sx={{ color: "white" }} />
								<Box className="login-form-background" />
							</Box>
						) : requestOTPError ? (
							<Box className="login-form-section">
								<Box className="login-error-wrapper">
									<Box className="login-error">
										<Typography variant="body2" textAlign="center" color="var(--color-error-main)">
											{"data" in requestOTPError && requestOTPError.data
												? String(requestOTPError.data)
												: "Failed to send OTP"}
										</Typography>
									</Box>
								</Box>

								<Button variant="contained" color="success" onClick={handleRetryRequestOTP}>
									Retry
								</Button>

								<Link variant="body2" textAlign="center" color="inherit" onClick={changeAccount}>
									Login with different account
								</Link>

								<Box className="login-form-background" />
							</Box>
						) : (
							<Box className="login-form-section">
								<Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0.5rem" }}>
									<Typography variant="h4" fontWeight={500}>
										Verify OTP
									</Typography>

									<Typography textAlign="center">
										We've sent a 6-digit verification code to your registered email
									</Typography>
								</Box>

								<Box className="login-form-wrapper" component="form" noValidate onSubmit={handleSubmitOTP}>
									<Box className="login-input-wrapper">
										<OTPInput length={6} value={otp} onChange={setOtp} />
									</Box>

									{verifyLoginOTPError ? (
										<Box className="login-error-wrapper">
											<Box className="login-error">
												<Typography variant="body2" textAlign="center" color="var(--color-error-main)">
													{"data" in verifyLoginOTPError && verifyLoginOTPError.data
														? String(verifyLoginOTPError.data)
														: "Something went wrong"}
												</Typography>
											</Box>
										</Box>
									) : null}

									<LoadingButton
										disableElevation
										variant="contained"
										color="success"
										sx={{ paddingY: 1.5, textTransform: "capitalize", marginTop: "1rem" }}
										type="submit"
										loading={verifyLoginOTPLoading}
										disabled={otp.length !== 6}
									>
										Verify OTP
									</LoadingButton>

									<Typography variant="body2" textAlign="center">
										Didn't receive the code?{" "}
										{resendTimer > 0 ? (
											<Typography component="span" variant="body2" color="inherit">
												Resend in {resendTimer}s
											</Typography>
										) : (
											<Link color="inherit" onClick={handleRetryRequestOTP} sx={{ cursor: "pointer" }}>
												Resend
											</Link>
										)}
									</Typography>

									<Link variant="body2" textAlign="center" color="inherit" onClick={changeAccount}>
										Login with different account
									</Link>
								</Box>

								<Box className="login-form-background" />
							</Box>
						)
					) : (
						<Box className="login-form-section">
							<Typography variant="h4" fontWeight={500}>
								Enter Login Details
							</Typography>
							<Box className="login-form-wrapper" component="form" noValidate onSubmit={handleSubmit(handleSubmitForm)}>
								<Box className="login-input-wrapper">
									<Typography variant="h6" fontWeight={400} color="inherit">
										Username
									</Typography>

									<TextField
										required
										fullWidth
										autoFocus
										variant="filled"
										color="primary"
										placeholder="Enter username"
										autoComplete="off"
										slotProps={{
											input: { disableUnderline: true, hiddenLabel: true, classes: { root: "login-input-root" } }
										}}
										error={!!errors.username}
										helperText={errors.username && errors.username.message ? errors.username.message.toString() : ""}
										{...register("username", { required: "Username is required" })}
									/>
								</Box>

								<Box className="login-input-wrapper">
									<Typography variant="h6" fontWeight={400} color="inherit">
										Password
									</Typography>

									<TextField
										required
										fullWidth
										variant="filled"
										color="primary"
										placeholder="Enter password"
										type={showPassword ? "text" : "password"}
										autoComplete="off"
										slotProps={{
											input: {
												disableUnderline: true,
												hiddenLabel: true,
												classes: { root: "login-input-root" },
												endAdornment: (
													<InputAdornment position="end">
														<IconButton edge="end" onClick={handleToggleShowPassword} tabIndex={-1}>
															{showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
														</IconButton>
													</InputAdornment>
												)
											}
										}}
										error={!!errors.password}
										helperText={errors.password && errors.password.message ? errors.password.message.toString() : ""}
									{...register("password", { required: "Password is required" })}
								/>
							</Box>

							{authState.currentAction === "auth/loginUser" && authState.error ? (
									<Box className="login-error-wrapper">
										<Box className="login-error">
											<Typography variant="body2" textAlign="center" color="var(--color-error-main)">
												{authState.error.toLowerCase().includes("suspicious activity")
													? "Security check failed. Please try again in a few minutes."
													: authState.error}
											</Typography>
										</Box>
									</Box>
								) : null}

								<LoadingButton
									disableElevation
									variant="contained"
									color="success"
									sx={{ paddingY: 1.5, textTransform: "capitalize" }}
									type="submit"
									loading={authState.currentAction === "auth/loginUser" && authState.loading}
								>
									Login
								</LoadingButton>
							</Box>

							<Box className="login-form-background" />
						</Box>
					)}
				</Box>
			</Container>
		</Box>
	);
};

export default Login;
